/**
 * useFieldDrain (Inventory Screens Step 4.3b + 4.3c-i; counts LIVE since
 * 5.4 A-1) — auto-trigger, sync cycle (drain → reconcile), and live status
 * counts for the field zone.
 *
 * Cycle sequence per trigger (mount, connectivity-restore 'online', and —
 * Gate 3 — every successful enqueue via the tmg-field-enqueued window event,
 * so an item Saved while already online drains immediately instead of
 * sitting pending until the next mount):
 *   1. drainFieldQueue(callerEmail)   — ONLY when a callerEmail exists (the
 *      offline-gate mode has none; a caller-less drain would burn retry
 *      counts on 403s). Self-gates on navigator.onLine internally.
 *   2. reconcileConfirmations()       — ALWAYS, auth-state-independent and
 *      pure-local: a `saved` item stranded by a crash between mark and
 *      reconcile last session gets spent on next boot, even offline.
 *
 * Counts (5.4 A-1; source fixed in A-2): derived in plain synchronous code
 * from the SHARED zone-clean subscription (useFieldQueue — see its zone
 * contract; the old per-hook useLiveQuery-over-getQueue querier crossed a
 * foreign await before the read and froze at its mount snapshot). Every
 * syncQueue write (enqueue, drain mark, reconcile dequeue) re-fires the
 * shared subscription, so `pending` bumps the instant a capture is Saved
 * (no cycle wait) and `saved` trends to 0 live as items reconcile-and-
 * dequeue; "saved" still means saved-not-yet-reconciled. The transient
 * `syncing` flag is cycle-scoped React state (not table-derived) merged
 * into the returned object.
 *
 * Concurrency: module-level isCycling latch spans the WHOLE cycle — the
 * drain's own latch (drainLatch.js, set by drainFieldQueue) only guards the
 * network half, so without this a double-fired trigger would run reconcile
 * twice. One latch for the path, shared across remounts; drain's internal
 * latch remains as defense-in-depth. The per-mount `alive` flag guards
 * setState-after-unmount (FieldZonePlaceholder remounts per navigation).
 * Each half is caught independently and the latch resets in finally — a
 * drain or reconcile throw can neither strand the latch nor wedge the
 * syncing flag. A trigger that lands MID-cycle is banked (recycleRequested,
 * Gate 3) and spends as exactly one follow-up cycle when the running one
 * settles — a create-flush enqueue burst or the D.3 mid-reconcile follow-up
 * capture can no longer strand until the next mount.
 *
 * Returns { pending, saved, conflict, not_found, error, failed, syncing, halted,
 * haltCause, dirtyCount } — dirtyCount (F-40 Phase 2) counts RECORDS holding unsent
 * edits, from its own zone-clean liveQuery over the records table; it sits
 * deliberately outside the status counts, which describe QUEUE items. —
 * Step 5's field SyncChip reads this.
 * halted (Phase 2 UI arms) is the drain summary's own flag, captured rather
 * than recomputed. TRUE means one cycle stopped early with items still
 * pending. haltCause (B-035) says WHY: the summary's haltCause, one of the
 * drain's HALT_CAUSES ('no_token', 'token_refused', 'transport') when halted,
 * null otherwise. Nothing renders it yet.
 *
 * Option { onRekeyed } (P2-2 B): called once per cycle with reconcile's
 * non-empty `rekeyed` array ({ pending_id, record } pairs). Never called with
 * an empty or missing array, so a consumer that omits it — or a reconcile
 * that returns no such key — behaves exactly as before.
 */

import { useState, useEffect, useRef } from 'react';
import { useUser } from '../../../shared/context/UserContext';
import { drainFieldQueue } from './drainFieldQueue';
import { reconcileConfirmations } from './reconcileConfirmations';
import { FIELD_ENQUEUED_EVENT } from './fieldOutbox';
import { useFieldQueue } from './useFieldQueue';
import { useLiveQuery } from 'dexie-react-hooks';
import { fieldDb } from '../db/fieldDb';
import { isDirtyRecord } from '../db/fieldStore';

const EMPTY_COUNTS = {
  pending: 0, saved: 0, conflict: 0, not_found: 0, error: 0, failed: 0,
};

function countsOf(items) {
  const counts = { ...EMPTY_COUNTS };
  for (const item of items) {
    if (counts[item.status] !== undefined) counts[item.status] += 1;
  }
  return counts;
}

// Spans drain + reconcile as ONE admitted pass (see header).
let isCycling = false;
// Gate 3: a trigger bouncing off the latch must not be dropped — it banks
// here and the settling cycle's finally runs exactly ONE follow-up
// ('recycle'). Cleared BEFORE the follow-up fires, so only triggers landing
// during the follow-up re-arm it (correct: that's more work to drain).
let recycleRequested = false;

export function useFieldDrain({ onRekeyed } = {}) {
  const { callerEmail } = useUser();
  const [syncing, setSyncing] = useState(false);
  // P2-2 B: the consumer's re-key sink, read through a ref so the cycle
  // effect below never re-subscribes on a new function identity. No state is
  // set for re-keys here — a cycle that re-keys nothing renders nothing new.
  const onRekeyedRef = useRef(onRekeyed);
  onRekeyedRef.current = onRekeyed;
  // Phase 2 UI arms: the drain has always RETURNED `halted` (its summary
  // object, drainFieldQueue.js:383) and the call site has always thrown the
  // whole summary away. Nothing about the halt is reconstructible after the
  // fact: it marks no row, leaves retry_count untouched, and writes nothing
  // to IndexedDB, so a halt is a transient event and this useState is the
  // only place it can live. Cycle-scoped like `syncing`, and deliberately
  // NOT persisted -- a stale halt outliving the condition would be a worse
  // lie than the silence it replaces.
  const [halted, setHalted] = useState(false);
  // B-035: why the last cycle halted, or null. Written beside `halted`, under
  // exactly its update rules, so the two can never disagree. That includes
  // its gaps: neither is written when there is no callerEmail or the drain
  // throws, so both can outlive their condition (tracked separately).
  const [haltCause, setHaltCause] = useState(null);

  // Live count source (5.4 A-1, re-sourced A-2): the shared zone-clean
  // subscription; countsOf is plain per-render derivation ([] default →
  // EMPTY_COUNTS equivalent, so consumers always see the full key set).
  const items = useFieldQueue();
  const counts = countsOf(items);

  // -- dirtyCount (F-40 Phase 2) -- records holding unsent edits ---------
  // MIRRORS useFieldQueue's mechanism exactly: a Dexie useLiveQuery, so the
  // number moves the moment a commit lands (every scalar/child edit is an
  // immediate records.put — there is no debounce and no save-on-close), and
  // it drops back as Save enqueues and reconcile clears dirty to {}.
  //
  // ZONE CONTRACT — the same trap useFieldQueue.js:8-23 documents, and the
  // reason this does NOT call fieldStore.countDirtyRecords(): that helper
  // opens with `await ensureOpen()`, and a foreign await BEFORE the read
  // starts means useLiveQuery registers ZERO dependencies — the
  // subscription never re-fires and the value freezes at its mount
  // snapshot. That is the shipped 5.4 bug, and a frozen dirtyCount would
  // make this fix dishonest in exactly the way it exists to repair. So the
  // Dexie read starts SYNCHRONOUSLY here, and countDirtyRecords stays the
  // one-shot imperative reader for non-live callers — the same split
  // getQueue/useFieldQueue already draw. Never add an await, an async
  // wrapper, or a try/catch ahead of the read.
  //
  // The predicate is fieldStore.isDirtyRecord — imported, never re-stated,
  // so the live count and the imperative count can never disagree.
  const countDirty = () => fieldDb.records.filter(isDirtyRecord).count();
  const dirtyCount = useLiveQuery(
    () => (fieldDb.isOpen() ? countDirty() : fieldDb.open().then(countDirty)),
    [],
    0,
  );

  useEffect(() => {
    let alive = true;

    async function runCycle(trigger) {
      if (isCycling) { recycleRequested = true; return; }
      // The latch MUST be set inside this try. The finally below is the
      // only place it is cleared, and a throw before the try leaks it
      // true permanently at module scope — wedging every future cycle
      // with no log and no timer to recover. Do not hoist this brace.
      try {
        isCycling = true;
        console.log(`[fieldQueue] sync cycle admitted: ${trigger}`);
        if (alive) setSyncing(true);
        if (callerEmail) {
          try {
            const summary = await drainFieldQueue(callerEmail);
            if (alive) {
              setHalted(!!summary?.halted);
              setHaltCause(summary?.haltCause ?? null);
            }
          } catch (drainErr) {
            console.error('[fieldQueue] drain failed (cycle continues):', drainErr);
          }
        }
        try {
          const reconciled = await reconcileConfirmations();
          const pairs = reconciled?.rekeyed;
          if (alive && Array.isArray(pairs) && pairs.length > 0 && onRekeyedRef.current) {
            try {
              onRekeyedRef.current(pairs);
            } catch (deliverErr) {
              console.error('[fieldQueue] rekeyed delivery failed (cycle continues):', deliverErr);
            }
          }
        } catch (reconcileErr) {
          console.error('[fieldQueue] reconcile failed (cycle continues):', reconcileErr);
        }
      } finally {
        isCycling = false;
        if (alive) setSyncing(false);
        // Gate 3: spend a banked mid-cycle trigger as one follow-up cycle.
        // (If unmounted, the flag survives — the next mount cycle covers it.)
        if (alive && recycleRequested) {
          recycleRequested = false;
          runCycle('recycle').catch((e) =>
            console.warn('[fieldQueue] recycle threw:', e)
          );
        }
      }
    }

    // Mount cycle runs unconditionally: the drain self-gates on connectivity
    // and callerEmail; reconcile is pure-local and must run even offline.
    runCycle('mount');

    function handleOnline() {
      console.log('[fieldQueue] connectivity restored, cycling...');
      runCycle('online');
    }
    // Gate 3: enqueue nudge — byte-for-byte sibling of the online listener.
    function handleEnqueued() {
      runCycle('enqueue');
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener(FIELD_ENQUEUED_EVENT, handleEnqueued);
    return () => {
      alive = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(FIELD_ENQUEUED_EVENT, handleEnqueued);
    };
  }, [callerEmail]);

  // dirtyCount rides ALONGSIDE the status counts, never inside them: a
  // dirty record is not a queued item. Folding it into the counts object
  // would put it in countsOf's status keyspace, and folding it into
  // inFlight would claim the work is in the pipeline when the whole point
  // is that it is not.
  return { ...counts, syncing, halted, haltCause, dirtyCount };
}
